import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import { Flex, Text } from 'Components/Base';
import { useConfig } from 'Context/ConfigContext';
import { useVideoPlayer } from 'Context/VideoPlayer';
import { useClassroomNavigation } from 'Util/Hooks';
import { SUBSECTION_TYPE } from 'Components/Classroom/Content';

const NextCourseTitle = styled((props) => (
  <Text textStyle="body2" fontWeight={'normal'} px={6} mx={'auto'} {...props} />
))`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AutoToNextSubsectionListener = () => {
  const { classroom } = useConfig();
  const { goToNextSubsection, isLastSubsectionInCourse, currentSubsection, nextSubsection } =
    useClassroomNavigation();
  const isVideoSubsection =
    currentSubsection?.contentType === SUBSECTION_TYPE.VIDEO ||
    currentSubsection?.contentType === SUBSECTION_TYPE.VIDEO_SLIDE;
  const autoNextSubsectionOnVideoEnd = classroom?.autoNextSubsectionOnVideoEnd?.enable ?? false;
  const leadTime = classroom?.autoNextSubsectionOnVideoEnd?.leadTime ?? 7000;
  const leadTimeRatio = leadTime / 10;
  const shouldTriggerAutoNext =
    autoNextSubsectionOnVideoEnd && isVideoSubsection && nextSubsection?.canPreview;
  const { videoState, VIDEO_STATE, player } = useVideoPlayer();
  const actionTimeRef = useRef(null);
  const [percentage, setPercentage] = useState(0);

  useEffect(() => {
    if (isLastSubsectionInCourse) return;
    if (!shouldTriggerAutoNext) return;
    if (player !== null && videoState === VIDEO_STATE.ENDED) {
      actionTimeRef.current = setInterval(
        () =>
          setPercentage((prevPercent) => {
            const percent = prevPercent + 1;
            if (percent >= leadTimeRatio) return leadTimeRatio;
            return percent;
          }),
        10
      );
    }
    return () => {
      clearInterval(actionTimeRef.current);
      actionTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoState]);

  useEffect(() => {
    if (percentage === leadTimeRatio) {
      clearInterval(actionTimeRef.current);
      actionTimeRef.current = null;
      setPercentage(0);
      goToNextSubsection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentage]);

  useEffect(() => {
    const cancelAutoNextSubsectionOnLeavingPage = () => {
      if (document.visibilityState === 'hidden' && actionTimeRef.current) {
        clearInterval(actionTimeRef.current);
        actionTimeRef.current = null;
        setPercentage(0);
        return;
      }
    };
    const cancelAutoNextSubsectionOnMessage = (event) => {
      const message = event?.data;
      if (message === 'MODAL_INTERRUPT' && actionTimeRef.current) {
        clearInterval(actionTimeRef.current);
        actionTimeRef.current = null;
        setPercentage(0);
        return;
      }
    };

    cancelAutoNextSubsectionOnLeavingPage();
    document.addEventListener('visibilitychange', cancelAutoNextSubsectionOnLeavingPage);
    window.addEventListener('message', cancelAutoNextSubsectionOnMessage);
    return () => {
      document.removeEventListener('visibilitychange', cancelAutoNextSubsectionOnLeavingPage);
      window.removeEventListener('message', cancelAutoNextSubsectionOnMessage);
    };
  }, []);

  if (isLastSubsectionInCourse) return null;
  if (!shouldTriggerAutoNext) return null;
  if (videoState !== VIDEO_STATE.ENDED) return null;
  if (!actionTimeRef.current) return null;

  const countdown = Math.floor(((leadTimeRatio - percentage) * 1) / 100);
  return (
    <Flex width={1} flexDirection="column">
      {countdown > 0 ? (
        <NextCourseTitle>
          จะเริ่มใน{' '}
          <Text color={'primary'} fontWeight={'semibold'} as={'span'}>
            {countdown}
          </Text>{' '}
          วินาที
        </NextCourseTitle>
      ) : (
        <NextCourseTitle>กำลังเข้าสู่หัวข้อถัดไป</NextCourseTitle>
      )}
    </Flex>
  );
};

export default AutoToNextSubsectionListener;
